import React, { FC } from 'react';
import Image from 'next/image';
import * as Styled from './DiagnosticsSteps.styles';
import * as TrackingService from '@modules/tracking/service';
import {
  Diagnostic,
  DiagnosticsAnswerType,
  DiagnosticsStepAnswers,
  DiagnosticsStepDetail,
} from '@modules/diagnostics/model';
import {
  getCurrentLocale,
  getInternationalizeStringOptValue,
  getInternationalizeStringValue,
} from '@shared/modules/translation/utils';
import { useRouter } from 'next/router';
import Title from '@shared/components/title/Title';
import DiagnosticsStepsImages from '@modules/diagnostics/components/steps/images/DiagnosticsStepsImages';
import DiagnosticsStepsVideo from '@modules/diagnostics/components/steps/video/DiagnosticsStepsVideo';
import { RequiredCustomLinkProps } from '@shared/components/link/CustomLink';
import HeaderTitle from '@shared/components/header/HeaderTitle';
import Seo from '@shared/components/seo/Seo';
import { useSendTask } from '@core/http/hooks';
import downloadIcon from '@assets/icons/download.svg';

function getAnswerLink(answer: DiagnosticsStepAnswers, diagnosticNumber: number): RequiredCustomLinkProps {
  switch (answer.answerType) {
    case DiagnosticsAnswerType.Solved:
      return {
        to: 'resolved',
        params: {
          diagnostic_number: diagnosticNumber,
          answer_id: answer.id,
        },
      };

    case DiagnosticsAnswerType.Breakdown:
      return {
        to: 'failure',
        params: {
          diagnostic_number: diagnosticNumber,
          answer_id: answer.id,
        },
      };

    case DiagnosticsAnswerType.Continue:
      return {
        to: 'step',
        params: {
          diagnostic_number: diagnosticNumber,
          step_id: answer.nextStepId,
        },
      };
  }
}

interface DiagnosticsStepsProps {
  diagnostics: Diagnostic;
  steps: DiagnosticsStepDetail;
}

const DiagnosticsSteps: FC<DiagnosticsStepsProps> = ({ diagnostics, steps }) => {
  const router = useRouter();
  const currentLocale = getCurrentLocale(router);

  const [, tracking] = useSendTask(TrackingService.tracking);

  const videoUrl = getInternationalizeStringValue(steps.videoUrl, currentLocale);

  const handleClick = (answerId: string) => () => tracking({ answerId: answerId });

  const questionTitle =
    steps.questionTitle != null ? getInternationalizeStringOptValue(steps.questionTitle, currentLocale) : null;

  const hasQuestion = questionTitle != null && steps.answers.length > 0;

  return (
    <Styled.DiagnosticsStepsContainer>
      <Seo title={getInternationalizeStringValue(steps.shortName, currentLocale)} />

      <HeaderTitle title={getInternationalizeStringValue(diagnostics.name, currentLocale)} />

      <Styled.DiagnosticsStepsContent>
        <Styled.DiagnosticsStepsContentTitle>
          {getInternationalizeStringValue(steps.shortName, currentLocale)}
        </Styled.DiagnosticsStepsContentTitle>

        {currentLocale === 'fr' && steps.detail.french ? (
          <Styled.DiagnosticsStepsContentDetail dangerouslySetInnerHTML={{ __html: steps.detail.french }} />
        ) : steps.detail.english ? (
          <Styled.DiagnosticsStepsContentDetail dangerouslySetInnerHTML={{ __html: steps.detail.english }} />
        ) : null}

        {videoUrl != null ? <DiagnosticsStepsVideo url={videoUrl} /> : null}

        {steps.images.length > 0 ? <DiagnosticsStepsImages images={steps.images} /> : null}
      </Styled.DiagnosticsStepsContent>

      {steps.documents.length > 0 ? (
        <Styled.DiagnosticsStepsDocumentsContainer>
          <Styled.DiagnosticsStepsDocumentsContent>
            {steps.documents.map((it, i) => (
              <Styled.DiagnosticsStepsDocumentsItem key={i}>
                <Styled.DiagnosticsStepsDocumentsText>
                  <Styled.DiagnosticsStepsDocumentsTitle>{it.name}</Styled.DiagnosticsStepsDocumentsTitle>

                  {it.shortDescription != null ? (
                    <Styled.DiagnosticsStepsDocumentsDescription>
                      {it.shortDescription}
                    </Styled.DiagnosticsStepsDocumentsDescription>
                  ) : null}
                </Styled.DiagnosticsStepsDocumentsText>

                <Styled.DiagnosticsStepsDocumentsImage>
                  <a href={it.resource.url} target="_blank" rel="noreferrer" download>
                    <Image src={downloadIcon} width={19} height={19} alt="Download" />
                  </a>
                </Styled.DiagnosticsStepsDocumentsImage>
              </Styled.DiagnosticsStepsDocumentsItem>
            ))}
          </Styled.DiagnosticsStepsDocumentsContent>
        </Styled.DiagnosticsStepsDocumentsContainer>
      ) : null}

      {hasQuestion ? (
        <Styled.DiagnosticsStepsAnswersContainer>
          <Styled.DiagnosticsStepsAnswersContent>
            {questionTitle != null ? <Title title={questionTitle} /> : null}

            <Styled.DiagnosticsStepsAnswersContentItems>
              {steps.answers.map((answer, index) => (
                <Styled.DiagnosticsStepsAnswersLink
                  {...getAnswerLink(answer, diagnostics.number)}
                  onClick={handleClick(answer.id)}
                  key={index}>
                  {getInternationalizeStringValue(answer.label, currentLocale)}
                </Styled.DiagnosticsStepsAnswersLink>
              ))}
            </Styled.DiagnosticsStepsAnswersContentItems>
          </Styled.DiagnosticsStepsAnswersContent>
        </Styled.DiagnosticsStepsAnswersContainer>
      ) : null}
    </Styled.DiagnosticsStepsContainer>
  );
};

export default DiagnosticsSteps;
